import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Eye, Users, CalendarBlank, TrendUp, Globe, House, Medal, GenderMale, GenderFemale, MapPin, Question } from "@phosphor-icons/react";

const DAY_ID = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const RANK_CLS = ["bg-[#FFB800] text-white", "bg-racing-muted text-white", "bg-[#B0763F] text-white"];

function SummaryCard({ label, value, sub, testid, accent = false }) {
    return (
        <div className="bg-white p-5" data-testid={testid}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">{label}</p>
            <p className={`mt-2 font-display text-4xl ${accent ? "text-racing-red" : "text-neutral-900"}`} data-testid={`${testid}-value`}>{value}</p>
            <p className="mt-1 text-xs text-racing-muted">{sub}</p>
        </div>
    );
}

function RankedList({ items, labelKey = "label", valueKey = "count", unit = "", testid, emptyText }) {
    if (!items || items.length === 0) {
        return <p className="text-sm text-racing-muted p-5" data-testid={`${testid}-empty`}>{emptyText}</p>;
    }
    const max = Math.max(...items.map((item) => item[valueKey]), 1);
    return (
        <div data-testid={testid}>
            {items.map((item, index) => (
                <div key={item[labelKey]} className="px-5 py-3 border-b border-racing-line last:border-0" data-testid={`${testid}-row-${index}`}>
                    <div className="flex items-center gap-3">
                        <span className={`w-7 h-7 flex items-center justify-center text-xs font-display shrink-0 ${RANK_CLS[index] || "bg-racing-surface text-racing-muted border border-racing-line"}`}>
                            {index + 1}
                        </span>
                        <span className="flex-1 min-w-0 text-sm font-medium text-neutral-900 truncate" data-testid={`${testid}-label-${index}`}>{item[labelKey]}</span>
                        <span className="text-sm font-display text-racing-red" data-testid={`${testid}-value-${index}`}>{item[valueKey]}{unit}</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-racing-surface">
                        <div className="h-full bg-racing-red/70 transition-all duration-500" style={{ width: `${(item[valueKey] / max) * 100}%` }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function StatsTab() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/admin/analytics")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Gagal memuat analitik"));
    }, []);

    if (!data) return <p className="text-racing-muted" data-testid="stats-loading">Memuat analitik...</p>;

    const maxViews = Math.max(...data.trend.map((d) => d.views), 1);
    const genderTotal = data.demographics.gender.reduce((sum, g) => sum + g.count, 0);

    return (
        <div data-testid="stats-tab">
            <h2 className="font-display uppercase text-2xl text-neutral-900 mb-2">Analitik Pengunjung</h2>
            <p className="text-sm text-racing-muted mb-8">Data pengunjung unik mulai terekam sejak fitur ini aktif. Statistik akan terisi bertahap seiring kunjungan masuk.</p>

            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-3"><Users size={16} /> Pengunjung Unik</p>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-racing-line border border-racing-line mb-10">
                <SummaryCard label="Hari Ini" value={data.visitors.today} sub={`${data.views.today} kali dibuka`} testid="analytics-today" accent />
                <SummaryCard label="7 Hari Terakhir" value={data.visitors.week} sub={`${data.views.week} kali dibuka`} testid="analytics-week" />
                <SummaryCard label="Bulan Ini" value={data.visitors.month} sub={`${data.views.month} kali dibuka`} testid="analytics-month" />
                <SummaryCard label="Tahun Ini" value={data.visitors.year} sub={`${data.views.year} kali dibuka`} testid="analytics-year" />
                <SummaryCard label="Total" value={data.visitors.total} sub={`${data.views.total} kali dibuka`} testid="analytics-total" />
            </div>

            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-3"><TrendUp size={16} /> Tren Kunjungan 30 Hari Terakhir</p>
            <div className="flex items-end gap-1 h-44 border border-racing-line bg-white p-5 mb-10" data-testid="analytics-trend">
                {data.trend.map((d) => {
                    const dt = new Date(d.date + "T00:00:00");
                    return (
                        <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1 h-full group" title={`${d.date}: ${d.views} kunjungan, ${d.visitors} pengunjung`}>
                            <span className="text-[9px] font-medium text-neutral-900 opacity-0 group-hover:opacity-100 transition-opacity">{d.views}</span>
                            <div
                                className="w-full bg-racing-red/75 hover:bg-racing-red transition-colors"
                                style={{ height: `${Math.max((d.views / maxViews) * 100, 3)}%` }}
                                data-testid={`analytics-trend-bar-${d.date}`}
                            />
                            {(dt.getDate() === 1 || d.date === data.trend[data.trend.length - 1].date) && (
                                <span className="text-[9px] uppercase tracking-widest text-racing-muted">{dt.getDate()}/{dt.getMonth() + 1}</span>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-3"><Eye size={16} /> Halaman Paling Sering Dikunjungi</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
                <div className="border border-racing-line bg-white" data-testid="analytics-public-section">
                    <p className="flex items-center gap-2 px-5 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                        <Globe size={14} /> Halaman Publik
                    </p>
                    <RankedList items={data.top_pages_public} labelKey="page" valueKey="views" unit="" testid="analytics-top-public" emptyText="Belum ada kunjungan halaman publik." />
                </div>
                <div className="border border-racing-line bg-white" data-testid="analytics-portal-section">
                    <p className="flex items-center gap-2 px-5 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                        <House size={14} /> Portal Orang Tua
                    </p>
                    <RankedList items={data.top_pages_portal} labelKey="page" valueKey="views" unit="" testid="analytics-top-portal" emptyText="Belum ada kunjungan portal orang tua." />
                </div>
            </div>

            <div className="border-t-2 border-racing-gold pt-6 mb-6">
                <h3 className="font-display uppercase text-xl text-neutral-900" data-testid="analytics-demographics-title">Demografi Pengunjung</h3>
                <p className="text-xs text-racing-muted mt-1">
                    Usia & jenis kelamin dihitung dari data anak pada akun orang tua yang login ke portal ({data.demographics.logged_in_parents} akun, {data.demographics.children_sample} anak). Pengunjung anonim publik tidak dapat diketahui usia/gendernya.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="border border-racing-line bg-white" data-testid="analytics-age-section">
                    <p className="flex items-center gap-2 px-5 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                        <Medal size={14} /> Top Rentang Usia (Anak)
                    </p>
                    <RankedList items={data.demographics.age_ranges} testid="analytics-age-ranges" unit=" anak" emptyText="Belum ada data — menunggu orang tua login ke portal." />
                </div>
                <div className="border border-racing-line bg-white" data-testid="analytics-gender-section">
                    <p className="flex items-center gap-2 px-5 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                        <GenderMale size={14} /> <GenderFemale size={14} /> Jenis Kelamin (Anak)
                    </p>
                    {data.demographics.gender.length === 0 ? (
                        <p className="text-sm text-racing-muted p-5" data-testid="analytics-gender-empty">Belum ada data — menunggu orang tua login ke portal.</p>
                    ) : (
                        <div className="grid grid-cols-1 divide-y divide-racing-line" data-testid="analytics-gender">
                            {data.demographics.gender.map((g, index) => (
                                <div key={g.label} className="flex items-center gap-3 px-5 py-4" data-testid={`analytics-gender-row-${index}`}>
                                    {g.label === "Laki-laki" ? <GenderMale size={20} className="text-racing-red" /> : g.label === "Perempuan" ? <GenderFemale size={20} className="text-racing-golddeep" /> : <Question size={20} className="text-racing-muted" />}
                                    <span className="flex-1 text-sm font-medium text-neutral-900">{g.label}</span>
                                    <span className="font-display text-xl text-racing-red">{g.count}</span>
                                    <span className="text-xs text-racing-muted w-12 text-right">{genderTotal ? Math.round((g.count / genderTotal) * 100) : 0}%</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="border border-racing-line bg-white" data-testid="analytics-member-location-section">
                    <p className="flex items-center gap-2 px-5 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                        <MapPin size={14} /> Lokasi Anggota (Alamat Terdaftar)
                    </p>
                    <RankedList items={data.demographics.member_locations} testid="analytics-member-locations" unit=" anak" emptyText="Belum ada data alamat dari pengunjung yang login." />
                </div>
                <div className="border border-racing-line bg-white" data-testid="analytics-ip-location-section">
                    <p className="flex items-center gap-2 px-5 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                        <Globe size={14} /> Lokasi Pengunjung (Perkiraan dari IP)
                    </p>
                    <RankedList items={data.demographics.ip_locations} testid="analytics-ip-locations" unit=" pengunjung" emptyText="Belum ada lokasi yang teridentifikasi." />
                    {data.demographics.ip_unknown > 0 && (
                        <p className="px-5 py-3 text-xs text-racing-muted border-t border-racing-line" data-testid="analytics-ip-unknown">
                            {data.demographics.ip_unknown} pengunjung belum teridentifikasi lokasinya (IP lokal atau geolokasi belum selesai).
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
